class ProductVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.selectedVariantId = null;
    this.quantity = 1;
    this.variants = [];
    this.productId = null;
    this._listenersSetup = false;
    this._clickHandler = null;
    this._changeHandler = null;
    this._skipAutoInit = false;
  }

  connectedCallback() {
    if (!this._skipAutoInit) {
      this.init();
    }
  }

  init() {
    this.productId = this.getAttribute('data-product-id');
    this.sectionId = this.getAttribute('data-section-id') || 'lsg-buybox';

    const variantsData = this.getAttribute('data-variants');
    if (variantsData) {
      this.variants = JSON.parse(variantsData);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');

    if (variantParam) {
      const variantIdNum = parseInt(variantParam, 10);
      const variant = this.variants.find((v) => v.id === variantIdNum);
      if (variant) {
        this.selectedVariantId = variantIdNum;
      }
    }

    if (!this.selectedVariantId) {
      const firstVariant = this.variants.find((v) => v.available) || this.variants[0];
      if (firstVariant) {
        this.selectedVariantId = firstVariant.id;
      }
    }

    this.setupEventListeners();
    this.setupPurchaseOfferListeners();
    this.syncPurchaseTypeUI();
  }

  getCurrencyCode() {
    return this.getAttribute('data-currency') || 'USD';
  }

  formatMoney(cents) {
    const v = Number(cents);
    if (Number.isNaN(v)) return '';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: this.getCurrencyCode(),
    }).format(v / 100);
  }

  /** Stay AI widget host when rendered inside this buybox (used by ATC integration). */
  getStayPurchaseOptionsHost() {
    return this.querySelector('purchase-options[data-stayai-subscription-widget]');
  }

  getStaySelectedSellingPlanId(stayHost) {
    const subRadio = stayHost.querySelector('input[name="purchaseType"][value="purchaseTypeSubscription"]');
    if (!subRadio || !subRadio.checked) return null;

    const select = stayHost.querySelector('select[name="stayai_selling_plan"]');
    if (!select || select.disabled) return null;

    const raw = (select.value || '').trim();
    return raw || null;
  }

  getSelectedSellingPlanId() {
    const variant = this.variants.find((v) => v.id === this.selectedVariantId);
    if (!variant || !variant.available) return null;

    const stayHost = this.getStayPurchaseOptionsHost();
    if (stayHost) {
      return this.getStaySelectedSellingPlanId(stayHost);
    }

    const purchase = this.querySelector('[name="purchase_type"]:checked');
    if (purchase && purchase.value === 'onetime') return null;

    const select = this.querySelector('select[name="selling_plan"]');
    if (!select || select.disabled) return null;
    if (!select.value) return null;

    return select.value.trim();
  }

  setupPurchaseOfferListeners() {
    if (this._changeHandler) {
      this.removeEventListener('change', this._changeHandler);
    }

    this._changeHandler = (e) => {
      if (e.target.matches('[name="purchase_type"]')) {
        this.syncPurchaseTypeUI();
      }
      if (e.target.matches('select[name="selling_plan"]')) {
        this.updateSubscriptionOfferFromSelect();
      }
    };

    this.addEventListener('change', this._changeHandler);
  }

  syncPurchaseTypeUI() {
    const purchase = this.querySelector('[name="purchase_type"]:checked');
    const details = this.querySelector('.lsg-offer-subscribe-details');
    const select = this.querySelector('select[name="selling_plan"]');

    if (!purchase || !details || !select) return;

    const isSub = purchase.value === 'subscription';

    details.classList.toggle('is-open', isSub);
    select.disabled = !isSub;

    if (isSub) {
      this.updateSubscriptionOfferFromSelect();
    } else {
      this.syncOnetimePriceLabel();
    }
  }

  syncOnetimePriceLabel() {
    const variant = this.variants.find((v) => v.id === this.selectedVariantId);
    const el = this.querySelector('.lsg-offer-onetime-price');
    if (!variant || !el) return;
    el.textContent = this.formatMoney(variant.price);
  }

  updateSubscriptionOfferFromSelect() {
    const select = this.querySelector('select[name="selling_plan"]');
    if (!select || select.disabled) return;

    const opt = select.selectedOptions[0];
    if (!opt) return;

    const price = Number(opt.dataset.price);
    let compare =
      opt.dataset.compareAtPrice != null && opt.dataset.compareAtPrice !== ''
        ? Number(opt.dataset.compareAtPrice)
        : null;

    const variant = this.variants.find((v) => v.id === this.selectedVariantId);
    if (
      (compare == null || Number.isNaN(compare) || compare <= price) &&
      variant &&
      variant.compare_at_price &&
      variant.compare_at_price > price
    ) {
      compare = variant.compare_at_price;
    }

    let pct = 0;
    if (compare != null && !Number.isNaN(compare) && compare > price) {
      pct = Math.round(((compare - price) / compare) * 100);
    }

    const badge = this.querySelector('.lsg-offer-save-badge');
    if (badge) {
      if (pct > 0) {
        badge.textContent = `SAVE ${pct}%`;
        badge.classList.remove('hidden');
      } else {
        badge.textContent = '';
        badge.classList.add('hidden');
      }
    }

    const compareEl = this.querySelector('.lsg-offer-compare');
    const priceEl = this.querySelector('.lsg-offer-price');
    if (priceEl) priceEl.textContent = this.formatMoney(price);

    if (compareEl) {
      if (compare != null && !Number.isNaN(compare) && compare > price) {
        compareEl.textContent = this.formatMoney(compare);
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }

    const benefitPct = this.querySelector('.lsg-offer-benefit-pct');
    if (benefitPct) benefitPct.textContent = String(pct);
  }

  setupEventListeners() {
    if (this._clickHandler) {
      this.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }

    this._clickHandler = (e) => {
      const variantButton = e.target.closest('.variant-option-button');
      if (variantButton) {
        e.preventDefault();
        e.stopPropagation();
        const variantId = variantButton.dataset.variantId;
        if (variantId) {
          this.selectVariant(variantId);
        }
        return;
      }

      const minusBtn = e.target.closest('.qty-minus');
      if (minusBtn && !minusBtn.disabled) {
        e.preventDefault();
        this.updateQuantity(this.quantity - 1);
        return;
      }

      const plusBtn = e.target.closest('.qty-plus');
      if (plusBtn && !plusBtn.disabled) {
        e.preventDefault();
        this.updateQuantity(this.quantity + 1);
        return;
      }

      const addToCartBtn = e.target.closest('.add-to-cart-btn');
      if (addToCartBtn && !addToCartBtn.disabled) {
        e.preventDefault();
        this.addToCart();
        return;
      }
    };

    this.addEventListener('click', this._clickHandler);
    this._listenersSetup = true;
  }

  async selectVariant(variantId) {
    const variantIdNum = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
    const variant = this.variants.find((v) => v.id === variantIdNum);
    if (!variant) return;

    this.selectedVariantId = variantIdNum;
    this.quantity = 1;

    const url = new URL(window.location);
    url.searchParams.set('variant', variantIdNum);
    window.history.pushState({ variant: variantIdNum }, '', url);

    await this.rerenderBuybox();

    this.dispatchEvent(
      new CustomEvent('variant-selected', {
        bubbles: true,
        detail: { variant },
      })
    );
  }

  async rerenderBuybox() {
    if (!this.productId) return;

    try {
      const url = new URL(window.location);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to fetch section');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const buyboxContainer = document.querySelector('.lsg-buybox');
      if (!buyboxContainer) return;

      const newBuybox = doc.querySelector('.lsg-buybox');
      if (!newBuybox) return;

      const priceQuantityContainer = buyboxContainer.querySelector('.lsg-buybox__product-price-quantity-container');
      const newPriceQuantityContainer = newBuybox.querySelector('.lsg-buybox__product-price-quantity-container');

      if (priceQuantityContainer && newPriceQuantityContainer) {
        priceQuantityContainer.innerHTML = newPriceQuantityContainer.innerHTML;

        const newSelector = priceQuantityContainer.querySelector('product-variant-selector');
        if (newSelector) {
          newSelector._skipAutoInit = true;
          newSelector.selectedVariantId = this.selectedVariantId;
          newSelector.quantity = this.quantity;
          newSelector.variants = this.variants;
          newSelector.productId = this.productId;
          newSelector.init();

          const qtyInput = newSelector.querySelector('.qty-input');
          if (qtyInput) {
            qtyInput.value = this.quantity;
          }

          this.replaceWith(newSelector);
          return;
        }
      }

      const whereToBuyContainer = buyboxContainer.querySelector('.lsg-buybox__where-to-buy-container');
      const newWhereToBuyContainer = newBuybox.querySelector('.lsg-buybox__where-to-buy-container');

      if (whereToBuyContainer && newWhereToBuyContainer) {
        whereToBuyContainer.innerHTML = newWhereToBuyContainer.innerHTML;
      }
    } catch (error) {
      console.error('Error re-rendering buybox:', error);
      this.updateDOMManually();
    }
  }

  updateDOMManually() {
    const variant = this.variants.find((v) => v.id === this.selectedVariantId);
    if (!variant) return;

    const variantButtons = this.querySelectorAll('.variant-option-button');
    variantButtons.forEach((button) => {
      if (button.dataset.variantId === String(this.selectedVariantId)) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });

    const formattedPrice = this.formatMoney(variant.price);

    const formattedComparePrice = variant.compare_at_price
      ? this.formatMoney(variant.compare_at_price)
      : null;

    this.updatePrice(formattedPrice, formattedComparePrice, variant.price, variant.compare_at_price);

    const qtyInput = this.querySelector('.qty-input');
    if (qtyInput && variant.inventory_quantity !== null) {
      const maxQty =
        variant.inventory_policy === 'deny' && variant.inventory_management === 'shopify'
          ? variant.inventory_quantity
          : 9999;
      qtyInput.dataset.max = maxQty;

      if (this.quantity > maxQty) {
        this.updateQuantity(maxQty);
      }
    }

    this.updateAddToCartState(variant.available);
    this.syncOnetimePriceLabel();
  }

  updatePrice(formattedPrice, formattedComparePrice = null, price = null, compareAtPrice = null) {
    const priceElement = this.querySelector('.variant-price');
    const comparePriceElement = this.querySelector('.variant-compare-price');
    const discountElement = this.querySelector('.variant-discount');

    if (priceElement && formattedPrice) {
      priceElement.textContent = formattedPrice;
    }

    if (formattedComparePrice && compareAtPrice && price && compareAtPrice > price) {
      if (comparePriceElement) {
        comparePriceElement.textContent = formattedComparePrice;
        comparePriceElement.style.display = '';
      }
      if (discountElement) {
        const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
        discountElement.textContent = `${discount}% OFF`;
        discountElement.style.display = '';
      }
    } else {
      if (comparePriceElement) comparePriceElement.style.display = 'none';
      if (discountElement) discountElement.style.display = 'none';
    }
  }

  updateQuantity(newQty) {
    const qtyInput = this.querySelector('.qty-input');
    if (!qtyInput) return;

    const maxQty = parseInt(qtyInput.dataset.max, 10) || 9999;
    const minQty = 1;

    if (newQty < minQty) newQty = minQty;
    if (newQty > maxQty) newQty = maxQty;

    this.quantity = newQty;
    qtyInput.value = newQty;
  }

  updateAddToCartState(isAvailable) {
    const addToCartBtn = this.querySelector('.add-to-cart-btn');
    const addToCartContainer = this.querySelector('.lsg-buybox__add-to-cart-container');
    const qtyMinus = this.querySelector('.qty-minus');
    const qtyPlus = this.querySelector('.qty-plus');
    const qtyInput = this.querySelector('.qty-input');
    const purchaseTypeSection = this.querySelector('.lsg-buybox__purchase-type');

    if (!addToCartBtn || !addToCartContainer) return;

    if (purchaseTypeSection) {
      purchaseTypeSection.hidden = !isAvailable;
    }

    if (isAvailable) {
      addToCartBtn.textContent = 'Add To Cart';
      addToCartBtn.disabled = false;
      addToCartBtn.classList.remove('cursor-not-allowed');
      addToCartBtn.classList.add('cursor-pointer', 'hover:bg-secondary-yellow');

      if (qtyMinus) qtyMinus.disabled = false;
      if (qtyPlus) qtyPlus.disabled = false;
      if (qtyInput) qtyInput.disabled = false;

      addToCartContainer.removeAttribute('disabled');
      addToCartContainer.classList.remove('opacity-50', 'pointer-events-none');
    } else {
      addToCartBtn.textContent = 'Out of Stock';
      addToCartBtn.disabled = true;
      addToCartBtn.classList.remove('cursor-pointer', 'hover:bg-secondary-yellow');
      addToCartBtn.classList.add('cursor-not-allowed');

      if (qtyMinus) qtyMinus.disabled = true;
      if (qtyPlus) qtyPlus.disabled = true;
      if (qtyInput) qtyInput.disabled = true;

      addToCartContainer.setAttribute('disabled', '');
      addToCartContainer.classList.add('opacity-50', 'pointer-events-none');
    }
  }

  addToCart() {
    if (!this.selectedVariantId) {
      alert('Please select a variant');
      return;
    }

    const variant = this.variants.find((v) => v.id === this.selectedVariantId);
    if (!variant || !variant.available) {
      alert('This variant is out of stock');
      return;
    }

    const sellingPlanRaw = this.getSelectedSellingPlanId();
    console.log(">>> lsg-product-variant-selector: addToCart: sellingPlanRaw", sellingPlanRaw);

    const sellingPlan = sellingPlanRaw ? Number(sellingPlanRaw) : null;

    if (window.CartApi && window.CartApi.addItemToCart) {
      window.CartApi.addItemToCart(this.selectedVariantId, this.quantity, sellingPlan);
    } else {
      this.addItemToCartDirect(this.selectedVariantId, this.quantity, sellingPlan);
    }
  }

  addItemToCartDirect(variantId, qty, sellingPlan) {
    const data = {
      id: variantId,
      quantity: Number(qty),
    };

    if (sellingPlan && !Number.isNaN(sellingPlan)) {
      data.selling_plan = sellingPlan;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then(() => {
        if (typeof initCart === 'function') {
          initCart();
        }

        this.dispatchEvent(
          new CustomEvent('added-to-cart', {
            detail: { variantId, quantity: qty, sellingPlan },
          })
        );
      })
      .catch((err) => {
        console.error(err);
        alert('Error adding item to cart');
      });
  }
}

customElements.define('product-variant-selector', ProductVariantSelector);